import { type TypedDocumentNode, gql } from '@apollo/client';
import { FormattedDate, FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandL, LabelL, LabelS } from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import { glossary } from '@sorare/core/src/lib/glossary';
import { isTypeFn } from '@sorare/core/src/lib/gql';

import { MilestoneItems } from './MilestoneItems';
import type { MilestonesDialog_commonGameEndOfSeasonCountdown } from './__generated__/index.graphql';
import season26 from './assets/season26.svg';

const Root = styled(Vertical).attrs({ center: true, gap: 5 })`
  padding: var(--double-unit) var(--double-unit) var(--unit) var(--double-unit);
`;
const StyledImg = styled.img`
  height: var(--quadruple-unit);
  width: auto;
`;

type Props = {
  open: boolean;
  onClose: () => void;
  endOfSeasonCountdown: MilestonesDialog_commonGameEndOfSeasonCountdown;
};
export const MilestonesDialog = ({
  open,
  onClose,
  endOfSeasonCountdown,
}: Props) => {
  const { countdownUntil, milestones } = endOfSeasonCountdown;

  const endOfSeasonDate = (
    <FormattedDate month="long" day="numeric" value={countdownUntil} />
  );
  const cardsCountTasks = milestones.filter(isTypeFn('CardsCountTask'));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      title={
        <LabelL className="text-center">
          <FormattedMessage
            id="MilestonesDialog.title"
            defaultMessage="Your Season 25 rewards"
          />
        </LabelL>
      }
      footer={
        <Vertical>
          <Button color="secondary" size="medium" onClick={onClose}>
            <FormattedMessage {...glossary.gotIt} />
          </Button>
        </Vertical>
      }
    >
      <Root className="text-center">
        <Vertical gap={2}>
          <StyledImg src={season26} alt="" />
          <BrandL>
            <FormattedMessage
              id="MilestonesDialog.mainText"
              defaultMessage="End-season rewards"
            />
          </BrandL>
          <LabelL color="var(--c-nd-600)">
            <FormattedMessage
              id="MilestonesDialog.firstSubtitle"
              defaultMessage="The season is ending soon!"
            />
          </LabelL>
          <LabelL color="var(--c-nd-600)">
            <FormattedMessage
              id="MilestonesDialog.secondSubtitle"
              defaultMessage="Make sure to reach the highest collection milestone before {endOfSeasonDate} to unlock the ultimate rewards*"
              values={{
                endOfSeasonDate,
              }}
            />
          </LabelL>
        </Vertical>
        <MilestoneItems cardsCountTasks={cardsCountTasks} />
        <LabelS color="var(--c-nd-600)">
          <FormattedMessage
            id="MilestonesDialog.thirdSubtitle"
            defaultMessage="*Rewards will be claimable after {endOfSeasonDate}"
            values={{
              endOfSeasonDate,
            }}
          />
        </LabelS>
      </Root>
    </Dialog>
  );
};

MilestonesDialog.fragments = {
  commonGameEndOfSeasonCountdown: gql`
    fragment MilestonesDialog_commonGameEndOfSeasonCountdown on CommonGameEndOfSeasonCountdown {
      countdownUntil
      milestones {
        id
        ... on CardsCountTask {
          id
          ...MilestoneItems_cardsCountTask
        }
      }
    }
    ${MilestoneItems.fragments.cardsCountTask}
  ` as TypedDocumentNode<MilestonesDialog_commonGameEndOfSeasonCountdown>,
};
